import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Info, AlertTriangle, CheckCircle2, Quote } from "lucide-react";

/**
 * Renders a list of "content blocks" produced by admins or the Content API.
 * Supported block types: heading, paragraph, image, table, callout, faq,
 * key_takeaways, quote, code, divider, list.
 *
 * Blocks are markdown-friendly: text fields accept markdown so external AI
 * agents can drop content in without needing to build HTML.
 */
export function BlockRenderer({ blocks }) {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return <div className="text-[#94a3b8] italic">This post has no content yet.</div>;
  }
  return (
    <>
      {blocks.map((b, i) => <Block key={i} block={b} idx={i} />)}
    </>
  );
}

function Block({ block, idx }) {
  const t = block?.type;
  if (t === "heading") return <HeadingBlock block={block} />;
  if (t === "paragraph") return <ParagraphBlock block={block} />;
  if (t === "image") return <ImageBlock block={block} />;
  if (t === "table") return <TableBlock block={block} />;
  if (t === "callout") return <CalloutBlock block={block} />;
  if (t === "faq") return <FaqBlock block={block} />;
  if (t === "key_takeaways") return <KeyTakeawaysBlock block={block} />;
  if (t === "quote") return <QuoteBlock block={block} />;
  if (t === "code") return <CodeBlock block={block} />;
  if (t === "list") return <ListBlock block={block} />;
  if (t === "divider") return <hr className="my-8 border-[#e2e8f0]" data-testid={`block-divider-${idx}`} />;
  return null;
}

function HeadingBlock({ block }) {
  const level = Math.min(4, Math.max(2, block.level || 2));
  const id = slugFor(block.text);
  const classes = {
    2: "font-display font-bold text-2xl sm:text-3xl text-[#0f1b2d] mt-10 mb-4",
    3: "font-display font-bold text-xl sm:text-2xl text-[#0f1b2d] mt-8 mb-3",
    4: "font-display font-bold text-lg text-[#0f1b2d] mt-6 mb-2",
  }[level];
  const Tag = `h${level}`;
  return <Tag id={id} className={classes} data-testid={`block-heading-${level}`}>{block.text}</Tag>;
}

function ParagraphBlock({ block }) {
  return (
    <div className="prose-content text-[#1e293b] text-[15px] sm:text-base leading-7 mb-4" data-testid="block-paragraph">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
        {block.text || ""}
      </ReactMarkdown>
    </div>
  );
}

function ImageBlock({ block }) {
  if (!block.src) return null;
  return (
    <figure className="my-6" data-testid="block-image">
      <img src={block.src} alt={block.alt || ""} className="w-full rounded-lg border border-[#e2e8f0]" loading="lazy" />
      {block.caption && (
        <figcaption className="mt-2 text-xs text-[#64748b] text-center">{block.caption}</figcaption>
      )}
    </figure>
  );
}

function TableBlock({ block }) {
  const headers = block.headers || [];
  const rows = block.rows || [];
  return (
    <div className="my-6 overflow-x-auto border border-[#e2e8f0] rounded-lg" data-testid="block-table">
      <table className="w-full text-sm">
        <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
          <tr>
            {headers.map((h, i) => (
              <th key={i} className="text-left px-4 py-2.5 font-semibold text-[#0f1b2d]">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#f9fafb]"}>
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-[#1e293b] align-top">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const CALLOUT_STYLES = {
  info:    { icon: Info,           border: "border-[#93c5fd]", bg: "bg-[#eff6ff]", text: "text-[#1e3a8a]", iconColor: "text-[#2563eb]" },
  warning: { icon: AlertTriangle,  border: "border-[#fcd34d]", bg: "bg-[#fefce8]", text: "text-[#78350f]", iconColor: "text-[#d97706]" },
  success: { icon: CheckCircle2,   border: "border-[#86efac]", bg: "bg-[#f0fdf4]", text: "text-[#14532d]", iconColor: "text-[#16a34a]" },
};

function CalloutBlock({ block }) {
  const style = CALLOUT_STYLES[block.variant] || CALLOUT_STYLES.info;
  const Icon = style.icon;
  return (
    <div className={`my-6 rounded-lg border ${style.border} ${style.bg} p-4 flex gap-3`} data-testid="block-callout">
      <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${style.iconColor}`} />
      <div className={`text-sm leading-relaxed ${style.text}`}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{block.text || ""}</ReactMarkdown>
      </div>
    </div>
  );
}

function FaqBlock({ block }) {
  const items = block.items || [];
  return (
    <div className="my-8 space-y-3" data-testid="block-faq">
      {items.map((it, i) => (
        <details key={i} className="group border border-[#e2e8f0] rounded-lg bg-white">
          <summary className="cursor-pointer list-none px-4 py-3 font-semibold text-[#0f1b2d] flex items-center justify-between">
            <span>{it.q}</span>
            <span className="text-[#64748b] group-open:rotate-180 transition">▾</span>
          </summary>
          <div className="px-4 pb-4 pt-1 text-sm text-[#475569] leading-relaxed">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{it.a || ""}</ReactMarkdown>
          </div>
        </details>
      ))}
    </div>
  );
}

function KeyTakeawaysBlock({ block }) {
  const items = block.items || [];
  return (
    <div className="my-8 rounded-xl border-2 border-[#2563eb]/25 bg-gradient-to-br from-[#eff6ff] to-white p-5 sm:p-6" data-testid="block-key-takeaways">
      <div className="text-xs font-semibold uppercase tracking-widest text-[#2563eb] mb-3">Key Takeaways</div>
      <ul className="space-y-2">
        {items.map((t, i) => (
          <li key={i} className="flex items-start gap-2 text-[#0f1b2d] text-sm leading-relaxed">
            <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#16a34a] flex-shrink-0" />
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function QuoteBlock({ block }) {
  return (
    <blockquote className="my-6 border-l-4 border-[#2563eb] pl-4 py-1 text-[#334155] italic" data-testid="block-quote">
      <Quote className="w-4 h-4 inline-block text-[#2563eb] mr-1 -mt-1" />
      {block.text}
      {block.cite && <cite className="block mt-1 text-xs text-[#64748b] not-italic">— {block.cite}</cite>}
    </blockquote>
  );
}

function CodeBlock({ block }) {
  return (
    <pre className="my-4 p-4 bg-[#0f1b2d] text-[#e2e8f0] rounded-lg overflow-x-auto text-xs" data-testid="block-code">
      <code>{block.text}</code>
    </pre>
  );
}

function ListBlock({ block }) {
  const items = block.items || [];
  const Tag = block.ordered ? "ol" : "ul";
  const listClass = block.ordered ? "list-decimal" : "list-disc";
  return (
    <Tag className={`${listClass} pl-6 my-4 space-y-1.5 text-[#1e293b] text-[15px]`} data-testid="block-list">
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </Tag>
  );
}

// --- Utilities ---
export function slugFor(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

/**
 * Extract H2/H3 headings from a block array to power the table of contents.
 */
export function extractTOC(blocks) {
  return (blocks || [])
    .filter((b) => b?.type === "heading" && (b.level === 2 || b.level === 3 || !b.level))
    .map((b) => ({ id: slugFor(b.text), text: b.text, level: b.level || 2 }));
}

const mdComponents = {
  a: (p) => <a {...p} className="text-[#2563eb] underline hover:text-[#1d4ed8]" target={p.href?.startsWith("http") ? "_blank" : undefined} rel={p.href?.startsWith("http") ? "noopener noreferrer" : undefined} />,
  strong: (p) => <strong {...p} className="font-semibold text-[#0f1b2d]" />,
  ul: (p) => <ul {...p} className="list-disc pl-6 my-2 space-y-1" />,
  ol: (p) => <ol {...p} className="list-decimal pl-6 my-2 space-y-1" />,
  li: (p) => <li {...p} className="leading-relaxed" />,
  p: (p) => <p {...p} className="mb-2" />,
  code: (p) => <code {...p} className="px-1.5 py-0.5 rounded bg-[#f1f5f9] text-[#0f1b2d] text-[13px]" />,
};
